import { INTERCOM_USER_JWT_COOKIE_NAME } from '../chromatic-lib/constants/Intercom';
import { getCookie, setCookie, unsetCookie } from './cookie';

let intercomReadyTimeout: NodeJS.Timeout | null = null;
const intercomExpiryBuffer = 1000 * 60 * 2; // 2 minutes

const intercomAppId = process.env.CHROMATIC_INTERCOM_APP_ID;

// use to prevent Intercom authentication from happening before Intercom is ready
export const intercomReady = (
  cb: () => void,
  retries: number = 0,
  // optional for testing
  observer?: (retries: number) => void
) => {
  if (typeof window.Intercom === 'function') {
    if (intercomReadyTimeout) {
      clearTimeout(intercomReadyTimeout);
      intercomReadyTimeout = null;
    }
    cb();
  } else {
    observer?.(retries);

    if (retries >= 10) {
      return;
    }
    intercomReadyTimeout = setTimeout(() => {
      intercomReady(cb, retries + 1, observer);
    }, 100);
    return intercomReadyTimeout;
  }
};

const decodeJwt = (jwt: string) => {
  // decode claims only
  const base64Jwt = jwt.split('.')[1];
  const userData = JSON.parse(atob(base64Jwt));

  return userData;
};

export const validateIntercomToken = (userId: string) => {
  const intercomJwt = getCookie(INTERCOM_USER_JWT_COOKIE_NAME);
  let expiresAt, expired;

  // if no intercom jwt is stored, fetch one
  if (!intercomJwt) {
    getAndStoreIntercomJwt();
    return;
  }

  // try to decode the token to determine if it's valid and not expired
  try {
    const userData = decodeJwt(intercomJwt);

    // if the encoded user id does not match the authenticated user id, fetch a new token
    if (userData.user_id !== userId) {
      unsetCookie(INTERCOM_USER_JWT_COOKIE_NAME);
      getAndStoreIntercomJwt();
    }

    // if we have trouble reading the token, fetch a new one
    if (!userData || !userData.exp || !userData.email || !userData.user_id) {
      getAndStoreIntercomJwt();
      return;
    }

    expiresAt = new Date(userData.exp * 1000 - intercomExpiryBuffer);
    expired = expiresAt < new Date();

    if (expired) {
      getAndStoreIntercomJwt();
      return;
    }
  } catch (err) {
    console.log('Error reading intercom token', err);
    getAndStoreIntercomJwt();
    return;
  }

  // reauthenticate with the current token
  intercomReady(() => {
    window.Intercom('boot', {
      app_id: intercomAppId,
      intercom_user_jwt: intercomJwt,
    });
  });
};

export const getAndStoreIntercomJwt = () =>
  fetch('/intercom-user-jwt', { method: 'GET' })
    .then((res: Response) => {
      if (!res.ok) {
        throw new Error(`Failed to fetch Intercom token: ${res.status} ${res.statusText}`);
      }
      return res.json();
    })
    .then((data: { token: string }) => {
      const userData = decodeJwt(data.token);
      setCookie(INTERCOM_USER_JWT_COOKIE_NAME, data.token, null, {
        expires: new Date(userData.exp * 1000),
      });

      // re-authenticate intercom session
      intercomReady(() => {
        window.Intercom('boot', {
          app_id: intercomAppId,
          intercom_user_jwt: data.token,
        });
      });
    })
    .catch((err) => {
      console.log('Error fetching intercom token', err);
    });
